import { createBrowserHistory, createMemoryHistory } from 'history';
import CoursewareItemProvider from 'services/coursewareItem/CoursewareItemProvider';
import AnnotationService from 'services/annotation/AnnotationsService';
import { MOTIVATION_IDENTIFYING } from 'services/annotation/motivations';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import {
  extractSubTarget,
  generateSubTargetPart,
  pathnameToTarget,
  targetToPathname,
} from './locationTranslators';
import expandSimplifiedTarget from './expandSimplifiedTarget';
import { INTERACTIVE } from '../coursewareItem/coursewareTypes';
import { getInteractiveById } from '../walkable';
import gtmService from '../../utils/gtm/gtmService';

export class RouteProvider {
  constructor() {
    this.subscribers = [];
  }

  initialize({ disableBrowserHistory }) {
    this.history = disableBrowserHistory ? createMemoryHistory() : createBrowserHistory();
    this.history.listen(this.onHistoryChange);
    this.onHistoryChange(this.history.location);
  }

  getCurrentTarget = async (pathname) => {
    const target = pathnameToTarget(pathname);
    const simplifiedTarget = {
      ...target,
      subTarget: extractSubTarget(pathname),
    };

    // TODO: Add target validation and error handling to revert invalid pathname changes
    let currentTarget = await expandSimplifiedTarget(simplifiedTarget);

    const {
      id,
      type,
      ancestry,
      subTarget,
    } = currentTarget;

    if (id && type) {
      let rootId = id;
      if (ancestry && ancestry.length) {
        const rootElement = ancestry[ancestry.length - 1];
        rootId = rootElement.elementId;
      }

      const rootState = await CoursewareItemProvider.getInCoursewareState(rootId);
      document.title = rootState.title;
    }

    // if ref and elementRef both are part of subTarget then no need to fetch annotations to find elementRef.
    if (subTarget?.ref && subTarget?.elementRef) {
      return currentTarget;
    }

    if (subTarget?.ref && ancestry?.length) {
      // Retrieve identifying annotations for deployment
      const annotationsService = await AnnotationService.build({ target: { id: ancestry[0].elementId } });
      const annotations = await annotationsService.getAnnotationsByMotivation(MOTIVATION_IDENTIFYING);

      // Search thru annotations for a match for the current screen and subTarget ref field
      const findMatchingIdentifyingRef = (body) => body.purpose === MOTIVATION_IDENTIFYING && body.value === subTarget.ref;
      const findMatchingAnnotation = (annotation) => annotation.target.id === id && annotation.body.find(findMatchingIdentifyingRef);
      const matchingAnnotation = annotations.find(findMatchingAnnotation);

      // If found replace the subTarget ref text value, with the matched elementRef value
      const elementRef = matchingAnnotation && matchingAnnotation.target.subTarget.elementRef;
      if (elementRef) {
        currentTarget = {
          ...currentTarget,
          subTarget: {
            elementRef,
          },
        };
      } else {
        // Otherwise push to the target to remove the invalid subtarget
        this.push(target, {});
      }
    }
    return currentTarget;
  }

  onHistoryChange = async (location) => {
    const isPOCV2Enabled = featureFlagService.is(featureKeys.LEARNSPACE_POC_V2);

    if (isPOCV2Enabled) {
      /*
      ======================================
      TODO - remove this setTimeout and handle properly
      ======================================
      The reason this setTimeout is here is because there is a race condition happening between
       1. CoursewareItemProvider -> loadDeploymentData -> saveI
       2. this onHistoryChange > getCurrentTarget > createCoursewareTarget > getElementTypeAndAncestry.

      IF loadDeploymentData finishes first, getElementTypeAndAncestry DONT need to make a new getElementTypeAndAncestry query.
      IF loadDeploymentData DOES NOT finish before getElementTypeAndAncestry, a new graphql query getElementTypeAndAncestry is made
      */
      setTimeout(async () => {
        this.currentTarget = await this.getCurrentTarget(location?.pathname);

        const {
          cohortId,
          deploymentId,
          id,
          type,
        } = this.currentTarget;

        if (id && type && type === INTERACTIVE) {
          let walkable;

          // If the interactive already exist inside cache, use cache instead of fetching
          if (isPOCV2Enabled) {
            walkable = await CoursewareItemProvider.getCourseWareItem(id);
          } else {
            const fnReferenceToFetchInteractiveData = getInteractiveById;
            const response = await fnReferenceToFetchInteractiveData({
              cohortId,
              deploymentId,
              interactiveId: id,
            });
            walkable = response.data.learn.cohort.deployment[0].interactive;
          }
          if (walkable) {
            // Push Page view events for every interactive
            // Init Telemetry page activity before pushing page view event
            gtmService.pushUserTelemetryPageActivity();
            gtmService.pushPageViewEvent({
              pageName: isPOCV2Enabled ? walkable.config.title : JSON.parse(walkable.config).title,
              courseSectionId: walkable.ancestry[0]?.elementId,
              contentId: walkable.id,
              contentType: walkable.elementType,
            });
          }
        }

        this.notifySubscribers();
      }, 50);
    } else {
      this.currentTarget = await this.getCurrentTarget(location?.pathname);

      const {
        cohortId,
        deploymentId,
        id,
        type,
      } = this.currentTarget;

      if (id && type && type === INTERACTIVE) {
        const fnReferenceToFetchInteractiveData = getInteractiveById;

        const response = await fnReferenceToFetchInteractiveData({
          cohortId,
          deploymentId,
          interactiveId: id,
        });

        const walkable = response.data.learn.cohort.deployment[0].interactive;
        if (walkable) {
          // Push Page view events for every interactive
          // Init Telemetry page activity before pushing page view event
          gtmService.pushUserTelemetryPageActivity();
          gtmService.pushPageViewEvent({
            pageName: JSON.parse(walkable.config).title,
            courseSectionId: walkable.ancestry[0].elementId,
            contentId: walkable.id,
            contentType: walkable.elementType,
          });
        }
      }

      this.notifySubscribers();
    }
  }

  subscribe = (listener, coursewareId) => {
    const subscription = { coursewareId, listener };
    this.subscribers.push(subscription);
    if (this.currentTarget) {
      listener(this.currentTarget);
    }
    return () => this.unsubscribeByCoursewareId(coursewareId);
  }

  unsubscribeByCoursewareId = (coursewareId) => {
    this.subscribers = this.subscribers.filter((element) => element.coursewareId !== coursewareId);
  }

  notifySubscribers = () => {
    this.subscribers.forEach((subscription) => subscription.listener(this.currentTarget));
  }

  push = (target, subTarget) => {
    const { history } = this;
    if (!history) {
      throw new Error('Route Provider hasn\'t been initialized yet');
    }

    const targetPortion = targetToPathname(target);
    const subTargetPortion = generateSubTargetPart(subTarget);
    const fullURL = `${targetPortion}${subTargetPortion}`;
    const { location: { pathname } } = history;
    if (fullURL !== pathname) {
      history.push(fullURL);
    } else {
      history.replace(fullURL);
    }
  }
}

export default new RouteProvider();
